/**
 * v3 case-level anomaly analytics contract (anomaly_cases_v3).
 * One row = case_id + scenario_id + pair_id (EXACT scenarios 1-5, new anomaly_v1).
 * Client-safe (no server imports) — shared by loader and UI.
 */

import type { TranslateFn } from '@/lib/i18n'

/** New 4-level anomaly scale (do NOT collapse to 3). Ordered strongest→weakest. */
export type AnomalyLevel = 'priority_review' | 'high' | 'elevated' | 'baseline'
export type MlLevel = 'high' | 'medium' | 'low'

export const ANOMALY_LEVEL_ORDER: AnomalyLevel[] = ['priority_review', 'high', 'elevated', 'baseline']

/**
 * Подписи уровней — фабрики от `t`, а не константы: текст переехал в словарь и
 * зависит от локали. Форма объекта сохранена (Record с теми же КЛЮЧАМИ), чтобы
 * потребители продолжали индексироваться кодом с бэкенда без изменений.
 *
 * КЛЮЧИ (`priority_review`, `high`, `same_unit_history_wagon`…) — коды витрины,
 * их переводить НЕЛЬЗЯ: по ним идёт сопоставление с данными.
 */
export function anomalyLevelLabels(t: TranslateFn): Record<AnomalyLevel, string> {
  return {
    priority_review: t('analytics.anomalyLevel.priority_review'),
    high: t('analytics.anomalyLevel.high'),
    elevated: t('analytics.anomalyLevel.elevated'),
    baseline: t('analytics.anomalyLevel.baseline'),
  }
}

export function mlLevelLabels(t: TranslateFn): Record<MlLevel, string> {
  return {
    high: t('analytics.mlLevel.high'),
    medium: t('analytics.mlLevel.medium'),
    low: t('analytics.mlLevel.low'),
  }
}

// Analyst-facing «Группа сравнения» (comparison group). Raw cohort id stays in technical details.
export function anomalyCohortLabels(t: TranslateFn): Record<string, string> {
  return {
    same_unit_history_wagon: t('analytics.cohort.same_unit_history_wagon'),
    same_unit_no_history_wagon: t('analytics.cohort.same_unit_no_history_wagon'),
  }
}

export interface AnomalyCaseRow {
  caseId: string
  scenarioId: number
  scenarioName: string | null
  pairId: string
  mappingStatus: string
  mappingRuleId: string
  candidateFamily: string
  physicalUnitType: string
  serveSource: 'new_pipeline'
  // ── anomaly (new model) ──
  anomalyScore: number | null
  anomalyPercentile: number | null
  anomalyLevel: AnomalyLevel
  anomalyCohort: string
  anomalyModelVersion: string
  anomalyBundleVersion: string
  anomalyReasons: string[]
  // ── ML / Track A (separate) ──
  mlScore: number | null
  mlLevel: MlLevel
  mlTrack: string
  mlCohort: string
  reviewTier: string
  reviewQueue: string
  // ── business facts (prod.transit_data pipeline) ──
  cargo: string | null
  cargoExport: string | null
  cargoEtsng: string | null
  cargoGng: string | null
  cargoCodeChanged: boolean | null
  route: string | null
  importCountryFrom: string | null
  importCountryTo: string | null
  exportCountryFrom: string | null
  exportCountryTo: string | null
  importDate: string | null
  exportDate: string | null
  daysBetween: number | null
  wagon: string | null
  container: string | null
  sameWagon: boolean | null
  importStationFrom: string | null
  importStationTo: string | null
  exportStationFrom: string | null
  exportStationTo: string | null
  importWeight: number | null
  exportWeight: number | null
  weightDiffPct: number | null
  importShipmentId: string | null
  exportShipmentId: string | null
  importSenderBin: string | null
  importReceiverBin: string | null
  importPayerCode: string | null
  exportSenderBin: string | null
  exportReceiverBin: string | null
  exportPayerCode: string | null
  samePayerCode: boolean | null
  sameReceiverBin: boolean | null
  importReceiverEqualsExportSender: boolean | null
}

// ── Quantitative anomaly evidence (anomaly_evidence_v1) ──────────────────────
// Produced by the anomaly service from the frozen feature table under a verified
// identity. The UI renders these numbers and NEVER derives its own: a reason the
// service could not back with real columns simply does not appear in `reasons`.

export type EvidenceStatus = 'complete' | 'partial' | 'unavailable'

export interface AnomalyReasonEvidence {
  code: string
  /** What is being compared, phrased by the service from business fields. */
  subject: string | null
  /** Value observed for this case in the current period. */
  observedValue: number | null
  /** Historical comparison value, when the reason has one. */
  baselineValue: number | null
  /** observed vs baseline, only when mathematically valid (never 1/0). */
  multiplier: number | null
  currentPeriodStart: string | null
  currentPeriodEnd: string | null
  baselinePeriodStart: string | null
  baselinePeriodEnd: string | null
  /** Size of the unit's own history behind the statement. */
  historyCount: number | null
  /** Size of the reference corpus the case was compared against. */
  corpusSize: number | null
  evidenceSource: string
}

export interface AnomalyExplanation {
  version: string
  evidenceStatus: EvidenceStatus
  reasons: AnomalyReasonEvidence[]
  /** Codes present in anomalyReasons that no verified numbers support. */
  unsupportedReasonCodes: string[]
}

/** Audit counters for the eligibility rule. Not rendered — diagnostics only. */
export interface AnomalyEligibilityAudit {
  totalSourceRows: number
  eligibleRows: number
  excludedIncompleteRoute: number
  excludedByReason: Record<string, number>
  eaeuInternalEligible: number
}

export interface AnomalyCasesListResponse {
  source: 'new'
  version: string
  total: number
  page: number
  pageSize: number
  rows: AnomalyCaseRow[]
}

export interface AnomalyCasesSummary {
  source: 'new'
  version: string
  totalCases: number
  uniquePairs: number
  byAnomalyLevel: Record<string, number>
  byAnomalyCohort: Record<string, number>
  byMlLevel: Record<string, number>
  reasonsWithCount: number
  reasonsWithoutCount: number
  /** Derived from mappingStatus by the service (was a hard-coded 368). */
  ambiguousCases: number
  anomalyModelVersion: string
  mlModelTrack: string
  eligibility?: AnomalyEligibilityAudit
}

/**
 * Соответствие кейса аномалии кейсу реестра Freelancity («Кейсы»).
 *
 * ПРОСТРАНСТВА НОМЕРОВ НЕЗАВИСИМЫ. `caseId` витрины аномалий — это НЕ номер
 * реестра: по 22 036 доказанным совпадениям у 12 685 (57.6 %) номера разные
 * (аномалия #5919, вагон 58055427, — это кейс реестра #5920). Сопоставление
 * выполняется офлайн по физической идентичности (сценарий + обе отправки +
 * вагон + обе метки времени, со сверкой ЕТСНГ и масс), никогда по номеру.
 *
 * `selectedCaseNumber` заполнен ТОЛЬКО при `status === 'EXACT'` И настроенной
 * целевой среде. Во всех прочих случаях он `null`, и интерфейс не показывает
 * НИЧЕГО — ни прочерка, ни «не найден». Ни локальный номер, ни внутренний
 * идентификатор под этим ярлыком не показываются никогда.
 */
export interface FreelanceCaseMapping {
  /** `NOT_EVALUATED` — реестр не читался; это НЕ «не найдено». */
  status: 'EXACT' | 'NOT_FOUND' | 'AMBIGUOUS' | 'NOT_EVALUATED'
  /** Среда, к которой относится `selectedCaseNumber`. `null` — цель не настроена. */
  environment: 'dev' | 'prod' | null
  /**
   * ВИДИМОЕ «№ КЕЙСА» реестра «Кейсы» — ровно то значение, которое пользователь
   * находит глазами в таблице. НЕ внутренний идентификатор API и НЕ номер
   * локального реестра: последний отличается у 57.6 % доказанных совпадений.
   * Внутренний идентификатор до фронта не доходит вовсе.
   */
  selectedCaseNumber: string | null
  /**
   * ВЕСЬ доказанный набор видимых «№ КЕЙСА» при `AMBIGUOUS`.
   *
   * Каждый номер прошёл ТОТ ЖЕ физический ключ и ТУ ЖЕ валидацию, что и единственный
   * кандидат статуса `EXACT`: это не похожие и не подобранные кейсы, а несколько записей
   * реестра на одну физическую перевозку. Победитель не выбирается — при `AMBIGUOUS`
   * `selectedCaseNumber` остаётся `null` намеренно.
   *
   * `null` у всех остальных статусов.
   */
  candidateCaseNumbers?: string[] | null
  matchKeyVersion?: string | null
}

export interface AnomalyCaseDetailResponse {
  source: 'new'
  /** Detail rows additionally carry `anomalyExplanation`; list rows do not. */
  row: AnomalyCaseRow & {
    anomalyExplanation?: AnomalyExplanation
    /** Опционально: доказанное соответствие кейсу реестра Freelancity. */
    freelanceCase?: FreelanceCaseMapping
  }
}

// ── Similar analytical cases (Phase 2) ───────────────────────────────────────
export interface SimilarityComponents {
  sameCohort: boolean
  sameCargo: boolean
  sameCorridor: boolean
  sameStations: boolean
  closeDays: boolean
  closeWeight: boolean
  closeAnomaly: boolean
  closeMl: boolean
  /** Weighted comparison points (NOT an ML/anomaly score). May be fractional. */
  similarityScore: number
}

export interface SimilarCaseRow {
  caseId: string
  scenarioId: number
  scenarioName: string | null
  pairId: string
  importDate: string | null
  exportDate: string | null
  daysBetween: number | null
  wagon: string | null
  container: string | null
  importShipmentId: string | null
  exportShipmentId: string | null
  cargo: string | null
  route: string | null
  importStationTo: string | null
  exportStationFrom: string | null
  importPayerCode: string | null
  exportPayerCode: string | null
  importWeight: number | null
  exportWeight: number | null
  weightDiffPct: number | null
  anomalyScore: number | null
  anomalyLevel: AnomalyLevel
  mlScore: number | null
  mlLevel: MlLevel
  similarity: SimilarityComponents
  /** «Совпало» — matched anchors/features, semantic anchors first. */
  matches: string[]
  /** «Отличается» — real differences only. */
  mainDifferences: string[]
}

export interface SimilarCasesResponse {
  source: 'new'
  caseId: string
  scenarioId: number
  total: number
  page: number
  pageSize: number
  rows: SimilarCaseRow[]
  note: string
}

// ── Phase 3: v3 cargo + recommendation aggregates ────────────────────────────
export interface CargoAggregateRow {
  cargo: string | null
  cargoEtsng: string | null
  totalCases: number
  distinctPairs: number
  priorityReview: number
  high: number
  elevated: number
  baseline: number
  avgAnomaly: number | null
  maxAnomaly: number | null
  avgMl: number | null
  topRoutes: { route: string; count: number }[]
  topReasons: { code: string; count: number }[]
  cohortHistory: number
  cohortNoHistory: number
}

export interface CargoAggregatesResponse {
  source: 'new'
  total: number
  rows: CargoAggregateRow[]
}

export interface RecommendationTopCase {
  caseId: string
  scenarioId: number
  anomalyScore: number | null
  anomalyLevel: AnomalyLevel
  mlScore: number | null
  mlLevel: MlLevel
  cargo: string | null
  route: string | null
}

export interface RecommendationGroupV3 {
  id: string
  title: string
  explanation: string
  count: number
  note: string
  /** Filter hint to open the Аномалии tab pre-filtered (fields the view supports). */
  filter: { anomaly_level?: string; ml_level?: string; anomaly_cohort?: string; reason?: string }
  topCases: RecommendationTopCase[]
}

export interface RecommendationsV3Response {
  source: 'new'
  groups: RecommendationGroupV3[]
}
